import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Fab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  SupervisorAccount as AdminIcon,
  School as FacultyIcon,
  Work as StaffIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { UserService } from '../../services/userService';
import { User, UserFormData } from '../../types';

interface UserStats {
  total: number;
  admins: number;
  faculty: number;
  staff: number;
  active: number;
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [userStats, setUserStats] = useState<UserStats>({ total: 0, admins: 0, faculty: 0, staff: 0, active: 0 });
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    password: '',
    role: 'staff',
    email: '',
    firstName: '',
    lastName: ''
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadUsers();
  }, [searchTerm, selectedRole]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      let result;
      
      if (searchTerm.trim()) {
        result = { users: await UserService.searchUsers(searchTerm), hasMore: false };
      } else if (selectedRole !== 'all') {
        result = { users: await UserService.getUsersByRole(selectedRole), hasMore: false };
      } else {
        result = await UserService.getUsers(100); // Load more for stats
      }

      let filteredUsers = result.users;
      
      if (selectedRole !== 'all' && !searchTerm.trim()) {
        filteredUsers = result.users.filter(user => user.role === selectedRole);
      }

      setUsers(filteredUsers);
      calculateStats(result.users);
    } catch (error) {
      showSnackbar('Failed to load users', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (allUsers: User[]) => {
    const stats = allUsers.reduce((acc, user) => {
      acc.total++;
      if (user.isActive) acc.active++;
      
      switch (user.role) {
        case 'admin': acc.admins++; break;
        case 'faculty': acc.faculty++; break;
        case 'staff': acc.staff++; break;
      }
      
      return acc;
    }, { total: 0, admins: 0, faculty: 0, staff: 0, active: 0 });
    
    setUserStats(stats);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      role: 'staff',
      email: '',
      firstName: '',
      lastName: ''
    });
    setShowPassword(false);
    setOpenDialog(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '', // Don't pre-fill password for security
      role: user.role,
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || ''
    });
    setShowPassword(false);
    setOpenDialog(true);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      // Validation for new users
      if (!editingUser) {
        if (!formData.username.trim()) {
          showSnackbar('Username is required', 'error');
          return;
        }
        if (!formData.password || formData.password.length < 6) {
          showSnackbar('Password must be at least 6 characters long', 'error');
          return;
        }
        if (!formData.email || !formData.email.trim()) {
          showSnackbar('Email is required for new users', 'error');
          return;
        }
      }

      if (editingUser) {
        await UserService.updateUser(editingUser.id, {
          role: formData.role as 'admin' | 'faculty' | 'staff',
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName
        });
        showSnackbar('User updated successfully', 'success');
      } else {
        await UserService.createUser(formData);
        showSnackbar('User created successfully', 'success');
      }
      setOpenDialog(false);
      loadUsers();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to save user', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await UserService.deleteUser(userToDelete.id);
      showSnackbar('User deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to delete user', 'error');
    }
  };

  const toggleUserStatus = async (user: User) => {
    try {
      await UserService.toggleUserStatus(user.id);
      showSnackbar(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`, 'success');
      loadUsers();
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to toggle user status', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <AdminIcon color="error" />;
      case 'faculty': return <FacultyIcon color="primary" />;
      case 'staff': return <StaffIcon color="info" />;
      default: return <StaffIcon />;
    }
  };

  const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (role) {
      case 'admin': return 'error';
      case 'faculty': return 'primary';
      case 'staff': return 'info';
      default: return 'default';
    }
  };

  // Mobile card view for users
  const UserCard = ({ user }: { user: User }) => (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {getRoleIcon(user.role)}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                @{user.username}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={user.isActive ? 'Active' : 'Inactive'}
            color={user.isActive ? 'success' : 'default'}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>
        
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
            color={getRoleColor(user.role)}
            size="small"
          />
          {user.email && (
            <Chip
              label={user.email}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
        
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Created: {user.createdAt.toLocaleDateString()}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            • Last Login: {user.lastLogin ? user.lastLogin.toLocaleDateString() : 'Never'}
          </Typography>
        </Stack>
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Edit User">
            <IconButton
              size="small"
              onClick={() => handleEditUser(user)}
              sx={{ color: 'primary.main' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={user.isActive ? 'Deactivate User' : 'Activate User'}>
            <IconButton
              size="small"
              onClick={() => toggleUserStatus(user)}
              sx={{ color: user.isActive ? 'warning.main' : 'success.main' }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete User">
            <IconButton
              size="small"
              onClick={() => handleDeleteUser(user)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        User Management
      </Typography>

      {/* Stats Cards */}
      <Stack 
        direction={isMobile ? "column" : "row"} 
        spacing={2} 
        sx={{ mb: 3, flexWrap: "wrap", justifyContent: "space-between" }}
      >
        <Card sx={{ flex: isMobile ? "none" : "1 1 18%" }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Total Users
            </Typography>
            <Typography variant="h4" color="primary">
              {userStats.total}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: isMobile ? "none" : "1 1 18%" }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Active Users
            </Typography>
            <Typography variant="h4" color="success.main">
              {userStats.active}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: isMobile ? "none" : "1 1 18%" }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Admins
            </Typography>
            <Typography variant="h4" color="error.main">
              {userStats.admins}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: isMobile ? "none" : "1 1 18%" }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Faculty
            </Typography>
            <Typography variant="h4" color="primary.main">
              {userStats.faculty}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: isMobile ? "none" : "1 1 18%" }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Staff
            </Typography>
            <Typography variant="h4" color="info.main">
              {userStats.staff}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <Paper sx={{ p: 3 }}>
        {/* Actions Bar */}
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', mb: 3, gap: 2 }}>
          <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ flex: 1 }}>
            <TextField
              size="small"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ minWidth: isMobile ? "100%" : 250 }}
            />
            <FormControl size="small" sx={{ minWidth: isMobile ? "100%" : 120 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                label="Role"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="faculty">Faculty</MenuItem>
                <MenuItem value="staff">Staff</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={loadUsers} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Stack>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleCreateUser}
            fullWidth={isMobile}
            sx={{ mt: isMobile ? 1 : 0 }}
          >
            Add User
          </Button>
        </Box>

        {/* Users Table */}
        {isMobile ? (
          // Mobile Card View
          <Box sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No users found
                </Typography>
              </Box>
            ) : (
              <>
                {users
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => (
                    <UserCard key={user.id} user={user} />
                  ))}
              </>
            )}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getRoleIcon(user.role)}
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              @{user.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          color={getRoleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.createdAt.toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.lastLogin ? user.lastLogin.toLocaleDateString() : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit User">
                          <IconButton size="small" onClick={() => handleEditUser(user)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isActive ? 'Deactivate User' : 'Activate User'}>
                          <IconButton 
                            size="small" 
                            onClick={() => toggleUserStatus(user)}
                            color={user.isActive ? 'warning' : 'success'}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteUser(user)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add user"
          onClick={handleCreateUser}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
        >
          <PersonAddIcon />
        </Fab>
      )}

      {/* User Form Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            margin: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              disabled={!!editingUser}
              required
              helperText={editingUser ? "Username cannot be changed" : "Enter a unique username"}
            />
            {!editingUser && (
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                helperText="Password must be at least 6 characters long"
                inputProps={{ minLength: 6 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <ViewIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="admin">Admin - Full system access</MenuItem>
                <MenuItem value="faculty">Faculty - Class management access</MenuItem>
                <MenuItem value="staff">Staff - Limited access</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              helperText="Required for Firebase authentication"
              required={!editingUser}
            />
            <Stack direction={isMobile ? "column" : "row"} spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ width: isMobile ? '100%' : 'auto' }}
            variant={isMobile ? 'outlined' : 'text'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ width: isMobile ? '100%' : 'auto' }}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{userToDelete?.username}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 